import {css, StyleSheet} from 'aphrodite';
import React, {useMemo} from 'react';

import {assert} from 'Asserts';
import {BenchlingExpressionFunction} from 'benchling-expressions/BenchlingExpressionTypes';
import {getDisplayNameFromFunctionSignature} from 'benchling-expressions/BenchlingFunctionSignatures';
import BenchlingConfig from 'BenchlingConfig';
import {AnalyticsMetadataForContext} from 'constants/AnalyticsConstants';
import {LeafType, isBenchlingTypeLeafType} from 'data-types/benchling-type-def/BenchlingTypeDef';
import {ComplexTypeOperator} from 'data-types/benchling-type-def/ComplexTypes';
import {LookupConfigSpec} from 'lookup-config/LookupConfigModalConstants';
import {ProcessGraphBuilderNode} from 'processes/graph-builder-processes/validation/ProcessGraphBuilderConstants';
import ConditionalLogicLookupChip from 'processes/modals/router-config/ConditionalLogicLookupChip';
import ConditionalLogicSchemaDropdown from 'processes/modals/router-config/ConditionalLogicSchemaDropdown';
import ConditionalLogicValueDropdown from 'processes/modals/router-config/ConditionalLogicValueDropdown';
import ConditionalLogicValueNumeric from 'processes/modals/router-config/ConditionalLogicValueNumeric';
import ConditionalLogicValueText from 'processes/modals/router-config/ConditionalLogicValueText';
import {getPartialConstant, getPartialLookup} from 'processes/modals/router-config/RouterConfigUtils';
import {Schema} from 'schemas/SchemaConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import Menu from 'ui-library/menus/Menu';
import {MenuHeight, MenuWidth} from 'ui-library/menus/MenuConstants';
import Option from 'ui-library/menus/Option';
import PlaceholderText from 'ui-library/PlaceholderText';
import {DeepPartial} from 'util/Types';

import {ProcessOutputSchemaGraphQL} from '../../../../graphql-fragments/processes/schemas/__generated/ProcessOutputSchemaGraphQL';
import {ProcessTaskSchemaGraphQL} from '../../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaGraphQL';
import {getAllowedOperatorAndValueInfo} from './getAllowedOperatorAndValueInfo';
import {
  sharedStyles,
  maybeCastToParentRouterLeafType,
  ALLOWED_ROUTING_OPERATORS_BY_FUNCNAME,
} from './RouterConfigConstants';

interface LogicalFunctionConfigErrors {
  isNextNodeDuplicate: boolean;
}

export interface LogicalFunctionConfigProps {
  routerNodeClientId: string;
  flowchartClientId: string;
  expression: DeepPartial<BenchlingExpressionFunction>;
  setExpression: (newExpression: DeepPartial<BenchlingExpressionFunction>) => void;
  inputNode: ProcessGraphBuilderNode;
  flowchartTaskSchema: Schema;
  inputSchema: ProcessTaskSchemaGraphQL | ProcessOutputSchemaGraphQL;
  analyticsMetadata: AnalyticsMetadataForContext;
  dataTestAttrs?: DataTestAttrs;
  errors: LogicalFunctionConfigErrors;
}

const OPERATOR_PLACEHOLDER_TEXT = 'select operator';
const VALUE_PLACEHOLDER_TEXT = 'select value';
export const TYPE_MISMATCH_ERROR_MSG = "This operator doesn't match the lookup value's data type.";

export default function LogicalFunctionConfig(props: LogicalFunctionConfigProps): JSX.Element {
  // Input for a single condition of the form [[lookup] [operator] [value]]

  const {lookupConfig, operatorDisplayName, selectorId, allowedOperators, isMultiplex, typeDef} =
    useMemo(() => {
      const partialLookup = getPartialLookup(props.expression);
      const partialLookupConfig = partialLookup?.lookupConfig;
      const lookupConfig = partialLookupConfig ? (partialLookupConfig as LookupConfigSpec) : null;

      const operatorName = props.expression?.name;
      assert(
        operatorName == null || ALLOWED_ROUTING_OPERATORS_BY_FUNCNAME[operatorName] != null,
        'Operator is not allowed',
        {operatorName}
      );

      const {
        isMultiplex,
        allowedOperators,
        selectorId = null,
        typeDef,
      } = lookupConfig
        ? getAllowedOperatorAndValueInfo(lookupConfig, props.inputSchema)
        : {isMultiplex: false, allowedOperators: null, selectorId: null, typeDef: null};
      const operatorDisplayName = operatorName
        ? getDisplayNameFromFunctionSignature(ALLOWED_ROUTING_OPERATORS_BY_FUNCNAME[operatorName], typeDef)
        : null;

      return {
        lookupConfig,
        operatorDisplayName,
        selectorId,
        allowedOperators,
        isMultiplex,
        typeDef,
      };
    }, [props.expression, props.inputSchema]);

  const getIsMismatchedTypeError = (): boolean => {
    if (props.expression?.name == null || allowedOperators == null) {
      return false;
    } else if (allowedOperators.find(({functionName}) => functionName === props.expression.name) == null) {
      return true;
    }
    const lookupExpression = getPartialLookup(props.expression);
    const rightHandExpression = getPartialConstant(props.expression);

    if (lookupExpression == null || rightHandExpression == null || typeDef == null) {
      return false;
    }
    const lookupItemType = isMultiplex
      ? {name: ComplexTypeOperator.LIST, ofType: typeDef, selectorId}
      : {name: typeDef, selectorId};
    const rightHandItemType = rightHandExpression.itemType;
    if (rightHandItemType == null) {
      return false;
    }

    if (typeDef === LeafType.DROPDOWN_OPTION && lookupItemType.selectorId !== rightHandItemType.selectorId) {
      return true;
    }

    const rightHandItemComplexBaseType =
      rightHandItemType.name === ComplexTypeOperator.LIST ? rightHandItemType.ofType : undefined;

    let complexItemBaseTypesMismatch;
    if (isMultiplex) {
      complexItemBaseTypesMismatch = lookupItemType.ofType !== rightHandItemComplexBaseType;
    } else {
      complexItemBaseTypesMismatch = rightHandItemComplexBaseType !== undefined;
    }

    if (complexItemBaseTypesMismatch) {
      return true;
    }

    return false;
  };

  const isMismatchedTypesError = getIsMismatchedTypeError();

  const rightHandInputComponent = (): JSX.Element | null => {
    if (
      !typeDef ||
      !isBenchlingTypeLeafType(typeDef) ||
      (typeDef !== LeafType.DROPDOWN_OPTION && isMultiplex) ||
      props.expression?.name === undefined
    ) {
      return (
        <SelectDropdown
          className={css(sharedStyles.routerInputControl)}
          displayName={<PlaceholderText value={VALUE_PLACEHOLDER_TEXT} />}
          disabled={true}
          menu={<Menu height={MenuHeight.MEDIUM} width={MenuWidth.FULL} />}
          dataTestAttrs={{component: 'ConditionalLogicConfig', element: 'valueDropdown'}}
        />
      );
    }
    // when there is no value to be selected (i.e. the operatior is "is present")
    else if (props.expression?.parameters?.length !== 2) {
      return null;
    }

    const coercedType = maybeCastToParentRouterLeafType(typeDef);

    switch (coercedType) {
      case LeafType.DROPDOWN_OPTION:
        return (
          <ConditionalLogicValueDropdown
            isLookupValueMultiplex={isMultiplex}
            selectorId={selectorId}
            expression={props.expression}
            setExpression={props.setExpression}
            constantExpression={getPartialConstant(props.expression)}
            dataTestAttrs={{component: 'ConditionalLogicConfig', element: 'valueDropdown'}}
          />
        );
      case LeafType.INTEGER:
      case LeafType.DECIMAL:
        return (
          <ConditionalLogicValueNumeric
            expression={props.expression}
            setExpression={props.setExpression}
            constantExpression={getPartialConstant(props.expression)}
          />
        );
      case LeafType.STORABLE:
      case LeafType.BIOENTITY:
      case LeafType.ENTRY:
      case LeafType.LOCATION:
      case LeafType.REQUEST:
      case LeafType.RESULT:
      case LeafType.RUN:
        return (
          <ConditionalLogicSchemaDropdown
            expression={props.expression}
            setExpression={props.setExpression}
            constantExpression={getPartialConstant(props.expression)}
          />
        );
      case LeafType.TEXT:
        return (
          <ConditionalLogicValueText
            expression={props.expression}
            setExpression={props.setExpression}
            constantExpression={getPartialConstant(props.expression)}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className={css(sharedStyles.row, styles.customRow)} {...getTestProps(props.dataTestAttrs)}>
      <ConditionalLogicLookupChip
        setExpression={props.setExpression}
        initialFunction={props.expression}
        initialLookupConfig={lookupConfig}
        flowchartTaskSchema={props.flowchartTaskSchema}
        inputNode={props.inputNode}
        flowchartClientId={props.flowchartClientId}
        inputSchema={props.inputSchema}
        routerNodeClientId={props.routerNodeClientId}
        analyticsMetadata={props.analyticsMetadata}
        dataTestAttrs={{component: 'ConditionalLogicConfig', element: 'conditionalLogicLookupChip'}}
      />
      <SelectDropdown
        className={css(sharedStyles.routerInputControl)}
        displayName={operatorDisplayName ?? <PlaceholderText value={OPERATOR_PLACEHOLDER_TEXT} />}
        disabled={lookupConfig == null}
        shouldEllipsize={true}
        error={
          isMismatchedTypesError && BenchlingConfig.WORKFLOWS_ENABLE_AND_OR_CONDITIONS
            ? TYPE_MISMATCH_ERROR_MSG
            : undefined
        }
        menu={
          <Menu height={MenuHeight.MEDIUM} width={MenuWidth.FULL}>
            {allowedOperators
              ? allowedOperators.map((operator, index) => (
                  <Option
                    key={operator.functionName}
                    value={operator.functionName}
                    onSelect={() => {
                      // We modify the parameter names to match those defined in our newly-selected function's signature.
                      const newParams = [
                        {
                          ...props.expression?.parameters?.[0],
                          name: operator.parameters[0].parameterName,
                        },
                      ];

                      assert(operator.parameters.length <= 2, 'Operator has too many parameters', {
                        operator,
                      });

                      if (operator.parameters.length > 1) {
                        if (isMismatchedTypesError) {
                          // If the previously selected operator was not one of the allowed operators we want to clear
                          // the right hand parameter since the type is likely not compatible with the newly
                          // selected operator.
                          newParams.push({
                            name: operator.parameters[1].parameterName,
                            expression: undefined,
                          });
                        } else {
                          newParams.push({
                            ...props.expression?.parameters?.[1],
                            name: operator.parameters[1].parameterName,
                          });
                        }
                      }

                      const newExpression = {
                        ...props.expression,
                        name: operator.functionName,
                        parameters: newParams,
                      };
                      props.setExpression(newExpression);
                    }}
                  >
                    {getDisplayNameFromFunctionSignature(operator, typeDef)}
                  </Option>
                ))
              : null}
          </Menu>
        }
        dataTestAttrs={{component: 'ConditionalLogicConfig', element: 'operatorDropdown'}}
      />
      {rightHandInputComponent()}
    </div>
  );
}

const styles = StyleSheet.create({
  customRow: {
    alignItems: 'flex-start',
    minWidth: '516px',
  },
});
